<template>
  <div class="position-relative">
    <div
      class="go-back-container"
      v-if="
        currectStep === 'REPORT_REASON' || currectStep === 'PHONE_CONFIRMATION'
      "
    >
      <img
        :src="`${useAsset('img/credit-line/back-button.png')}`"
        @click="handleGoBackAction"
        alt="back"
      />
      <span class="go-back-title">
        {{ t("CL_TRANSACTIONS.REPORT_TRANSACTION") }}
      </span>
    </div>
    <ReportTransaction
      v-if="currectStep === 'REPORT_REASON'"
      :error-message="errorMessage"
      :debit-id="debitId"
      @submit="handleReasonSubmit"
      :disableButton="disableButton"
    ></ReportTransaction>
    <!--    <FreezeCardPrompt-->
    <!--      v-else-if="submittedReason === 'NOT_MY_TRANSACTION' && !reportSubmitted"-->
    <!--      @freeze="handleFreezeCard"-->
    <!--      @just-report="handleJustReport"-->
    <!--    />-->
    <UserPhoneConfirmationForm
      v-else-if="currectStep === 'PHONE_CONFIRMATION'"
      @onContinueClick="createDispute"
      :disableButton="disableButton"
      :showBanner="false"
      :loading="isLoading"
      :title="t('CL_TRANSACTIONS.REPORT_TRANSACTION_PHONE_CONFIRMATION_TITLE')"
      :description="
        t('CL_TRANSACTIONS.REPORT_TRANSACTION_PHONE_CONFIRMATION_DESCRIPTION')
      "
    />
    <ReportSubmittedConfirmation
      v-else-if="currectStep === 'REPORT_SUBMITTED'"
      :card-frozen="cardFrozen"
      @close="handleReportSubmittedClose"
    />
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from "vue-router";
import ReportTransaction from "@/apps/credit-line/components/Transactions/ReportTransaction.vue";
import ReportSubmittedConfirmation from "@/apps/credit-line/components/Transactions/ReportSubmittedConfirmation.vue";
import { reportTransaction } from "@/apps/credit-line/services/transactions/reports.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { ref, onMounted } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import UserPhoneConfirmationForm from "@/apps/credit-line/components/CreditCard/UserPhoneConfirmationForm.vue";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { ICreditCardUserFormData } from "@/apps/credit-line/types/forms";

declare const Android: AndroidInterface;

type IReportTransactionsSteps =
  | "REPORT_REASON"
  | "PHONE_CONFIRMATION"
  | "REPORT_SUBMITTED";
const { t } = useI18n();

const route = useRoute();
const errorMessage = ref<string | null>(null);

// Ensure debitId is properly extracted from route params
const debitId = route?.params?.debitId;

const cardFrozen = ref<boolean>(false);
const reportSubmitted = ref(false);
const submittedReason = ref<string>("");
const otherDetailsValue = ref<string>("");
const isLoading = ref(false);
const currectStep = ref<IReportTransactionsSteps>("REPORT_REASON");

const disableButton = ref(false);

const handleReasonSubmit = async (reason: string, otherDetails: string) => {
  currectStep.value = "PHONE_CONFIRMATION";
  submittedReason.value = reason;
  otherDetailsValue.value = otherDetails;
  // TODO: Uncomment once cards can be frozen from the transaction report.
  // if (submittedReason.value !== "NOT_MY_TRANSACTION") {

  // }
};

const createDispute = async (data: ICreditCardUserFormData) => {
  isLoading.value = true;

  const phoneNumber = data.phoneNumber || "";

  await handleJustReport(
    submittedReason.value,
    "Phone: " + phoneNumber + " " + otherDetailsValue.value
  );

  // TODO: Uncomment once cards can be frozen from the transaction report.
  // if (submittedReason.value !== "NOT_MY_TRANSACTION") {

  // }
};

const hideNav = () => {
  if (Android && typeof Android?.hideNav === "function") {
    Android.hideNav();
  }
};

const handleJustReport = async (reason: string, otherDetails: string) => {
  try {
    errorMessage.value = null;
    await reportTransaction({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      reportReason: reason,
      otherDetails,
      debitId: debitId,
    });

    reportSubmitted.value = true;
    currectStep.value = "REPORT_SUBMITTED";
    isLoading.value = false;
  } catch (e: unknown) {
    console.error("Error submitting report", e);
    const err = e as {
      response?: {
        data?: { message?: string; instance?: string; errorCode?: number };
      };
    };
    if (err.response && err.response.data) {
      const { message, instance, errorCode } = err.response.data;
      if (message === "transaction is not eligible for reporting") {
        disableButton.value = true;
        errorMessage.value = t("CL_TRANSACTIONS.TRANSACTION_DISABLED");
        return;
      } else {
        disableButton.value = true;
        errorMessage.value = `Error: ${message}. Instance: ${instance}. Error Code: ${errorCode}`;
      }
    } else {
      errorMessage.value =
        "An unexpected error occurred while submitting the report.";
    }
  }
};

const handleGoBackAction = () => {
  if (currectStep.value === "REPORT_REASON") {
    router.back();
  } else if (currectStep.value === "PHONE_CONFIRMATION") {
    currectStep.value = "REPORT_REASON";
  }
};

const router = useRouter();
const handleReportSubmittedClose = () => {
  router.replace({
    name: "CreditLineTransactionsHistoryView",
  });
};

onMounted(() => {
  hideNav();
});
</script>

<style scoped>
.go-back-container {
  height: 56px;
  padding: 4px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #d5d5d5;
  font-family: Inter;
}

.go-back-title {
  font-size: 16px;
  font-weight: 600;
  margin-left: 12px;
  color: #000000;
}
</style>
